import React, { useState, useEffect, useCallback, useMemo ,useRef} from "react";
import { useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import PropertyCard from "./PropertyCard";
import AddRequirementModal from "./AddRequirementModal";
import { Button } from "./ui/button";
import {
  ArrowLeft,
  ArrowRight,
  Plus,
  RefreshCw,
  AlertTriangle,
  MapPin,
  CalendarDays,
  Building2,
  Home,
  Tag,
  Search,
  Pencil,
  Trash2,
  CheckCircle2,
} from "lucide-react";
import { toast } from "react-toastify";
import apiClient from "../common/apiClient";

// How many match requests run in parallel. Kept small because the match API is
// rate limited per user - see the 429 handling in PropertyCard.
const MATCH_CONCURRENCY = 5;

// Builder tab is hidden for now. Uncomment the entry below to bring it back -
// the builder bucket is still fetched and filtered, only the tab is gone.
const TABS = [
  { key: "owner", label: "Owner", bucket: "owner", sourceType: "owner" },
  { key: "broker", label: "Broker", bucket: "broker", sourceType: "broker" },
  // { key: "builder", label: "Builder", bucket: "builder", sourceType: "builders" },
];

const BUCKET_KEYS = ["owner", "broker", "builder"];

const EMPTY_BUCKETS = { owner: [], broker: [], builder: [] };

const toCount = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

// The match API has been seen returning the buckets either at the top level or
// nested under `data` / `result`, so probe each shape instead of guessing.
const pickBuckets = (candidate) => {
  if (!candidate || typeof candidate !== "object") return null;
  const hasBucket =
    Array.isArray(candidate.ownerProperties) ||
    Array.isArray(candidate.brokerProperties) ||
    Array.isArray(candidate.builderProperties);
  return hasBucket ? candidate : null;
};

const extractBuckets = (apiResult) => {
  const body = apiResult?.data;
  const payload =
    pickBuckets(body) || pickBuckets(body?.data) || pickBuckets(body?.result);

  return {
    owner: payload?.ownerProperties || [],
    broker: payload?.brokerProperties || [],
    builder: payload?.builderProperties || [],
  };
};

// Same unwrapping the requirements tab in Myproperty uses.
const extractRequirements = (apiResult) => {
  const body = apiResult?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.result)) return body.result;
  return [];
};

const requirementLabel = (requirement) =>
  requirement?.customerName || "";

// Card heading is the customer. Falls back to the premise name, then to
// something readable built from BHK + property type, like "2 BHK Apartment".
const requirementHeading = (requirement) => {
  if (requirement?.customerName) return requirement.customerName;
  if (requirement?.title) return requirement.title;

  const bhkList = Array.isArray(requirement?.bhk) ? requirement.bhk : [];
  const parts = [bhkList.join(", "), requirement?.subType].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "Untitled requirement";
};

// Requirement/customer id shown in the badge. Named differently across
// responses, so accept the common spellings.
const requirementCid = (requirement) =>
  requirement?.cid || requirement?.customerId || requirement?.customerID || "";

// Brokers identify a requirement by its CID, so the match chips and the filter
// dropdown lead with it and only fall back to the title when it is missing.
const requirementTag = (requirement) =>
  requirementCid(requirement) || requirementLabel(requirement);

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  if (Number.isNaN(number)) return null;
  return `₹${new Intl.NumberFormat("en-IN").format(number)}`;
};

const formatPriceRange = (priceRange) => {
  const min = formatMoney(priceRange?.min);
  const max = formatMoney(priceRange?.max);
  if (min && max) return `${min} – ${max}`;
  return min || max || "Price NA";
};

const formatSizeRange = (sizeRange) => {
  const min = sizeRange?.sqFt?.min;
  const max = sizeRange?.sqFt?.max;
  if (!min && !max) return null;
  if (min && max && min !== max) return `${min} – ${max} sqft`;
  return `${min || max} sqft`;
};

// Unread cutoff per requirement. The server owns it now
// (`matchesLastSeenAt`), so read state follows the broker across devices. A
// null field means never opened, in which case the cutoff is the start of
// today - nobody wants to land on a pile of historic "unread" matches.
const startOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const seenCutoff = (requirement) => {
  const stored = requirement?.matchesLastSeenAt;
  if (!stored) return startOfToday();

  const date = new Date(stored);
  return Number.isNaN(date.getTime()) ? startOfToday() : date;
};

// When the property actually turned up, which is what the unread badge keys
// off. Takes the LATEST of the two dates rather than the first one present:
// `createdAt` is missing from some match payloads, and `listedDate` is
// broker-entered so it gets backdated. Picking whichever field happened to be
// populated is what made the badge appear on some cards and not others.
const propertyMatchedAt = (property) => {
  const times = [property?.matchedAt, property?.matchMeta?.matchedAt, property?.listedDate, property?.createdAt]
    .map((value) => (value ? new Date(value).getTime() : NaN))
    .filter((time) => !Number.isNaN(time));

  return times.length > 0 ? new Date(Math.max(...times)) : new Date(0);
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Promise.all with a cap, so a broker with 30 requirements does not fire 30
// requests at once. Never rejects - failures come back as { ok: false }.
const runWithConcurrency = async (items, limit, worker) => {
  const results = new Array(items.length);
  let cursor = 0;

  const runner = async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      try {
        results[index] = { ok: true, value: await worker(items[index]) };
      } catch (error) {
        results[index] = { ok: false, error };
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, runner)
  );
  return results;
};

const MatchProperty = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState("owner");
  const [contactInfoMap, setContactInfoMap] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  // Requirement being edited / pending deletion. Both null when idle.
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [deletingRequirement, setDeletingRequirement] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [requirements, setRequirements] = useState([]);
  const [buckets, setBuckets] = useState(EMPTY_BUCKETS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [failedCount, setFailedCount] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [locallyReadProperties, setLocallyReadProperties] = useState(new Set());

  // Step and requirement filter both live in the URL so refresh and browser
  // back behave. Deep links from a requirement card (?requirement=<id>) land
  // straight on step 2.
  const requirementParam = searchParams.get("requirement") || "all";
  const step =
    searchParams.get("step") === "matches" || requirementParam !== "all" ? 2 : 1;

  // Fall back to "all" once we know the id is not one of the user's
  // requirements (deleted requirement, hand-edited URL), otherwise the filter
  // would hide everything.
  const selectedRequirementId =
    requirementParam !== "all" &&
    requirements.length > 0 &&
    !requirements.some((requirement) => requirement._id === requirementParam)
      ? "all"
      : requirementParam;

  const updateParams = (changes) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === null) next.delete(key);
      else next.set(key, value);
    });
    setSearchParams(next, { replace: true });
  };

  // What each requirement's cutoff was when this visit started. Opening a
  // requirement clears its badge immediately, so without this the "New" tags in
  // step 2 would vanish in the same click that revealed them.
  const unreadBaselineRef = useRef({});

  // Cutoffs come off the requirement documents, so they are the server's -
  // read state follows the broker to any device.
  const seenAtMap = useMemo(() => {
    const map = new Map();
    requirements.forEach((requirement) => {
      map.set(requirement._id, seenCutoff(requirement));
    });
    return map;
  }, [requirements]);

  const seenSince = useCallback(
    (requirementId) => seenAtMap.get(requirementId) || startOfToday(),
    [seenAtMap]
  );

  // Everything listed at or after the cutoff, counted across all three buckets.
  // `>=` not `>`: a never-opened requirement uses today's midnight as its
  // cutoff, and a date-only `listedDate` for today parses to exactly midnight,
  // so a strict comparison drops the very properties the badge is meant to
  // catch.
  const unreadCountForRequirement = useCallback(
    (requirementId) => {
      const since = seenSince(requirementId);

      return BUCKET_KEYS.reduce(
        (sum, bucketKey) =>
          sum +
          buckets[bucketKey].filter(
            (item) =>
              item._matchedFor?.some((entry) => entry.id === requirementId) &&
              propertyMatchedAt(item) >= since
          ).length,
        0
      );
    },
    [buckets, seenSince]
  );

  // Clears the badge locally first, then persists. The POST is not awaited -
  // navigation should never wait on a read receipt.
  const markRequirementSeen = async (requirementId) => {
    const affected =
      requirementId === "all"
        ? requirements
        : requirements.filter((requirement) => requirement._id === requirementId);

    if (affected.length === 0) return;

    const ids = new Set(affected.map((requirement) => requirement._id));
    const now = new Date().toISOString();

    // Snapshot the pre-click cutoffs so step 2 can still tag which rows were
    // unread, and so a failed POST can put them back.
    const previous = new Map(
      affected.map((requirement) => [
        requirement._id,
        requirement.matchesLastSeenAt ?? null,
      ])
    );

    affected.forEach((requirement) => {
      unreadBaselineRef.current[requirement._id] = seenCutoff(
        requirement
      ).toISOString();
    });

    setRequirements((current) =>
      current.map((requirement) =>
        ids.has(requirement._id)
          ? { ...requirement, matchesLastSeenAt: now }
          : requirement
      )
    );

    const result = await apiClient(
      "POST",
      `/cjidnvij/ceksfbuebijn/user/brokerrequirements/${requirementId}/matches/seen`,
      {},
      { headers: { Authorization: `Bearer ${Cookies.get("token")}` } }
    );

    if (result?.success) return;

    // Roll back rather than leave the broker thinking these were marked read.
    ids.forEach((id) => delete unreadBaselineRef.current[id]);
    setRequirements((current) =>
      current.map((requirement) =>
        ids.has(requirement._id)
          ? {
              ...requirement,
              matchesLastSeenAt: previous.get(requirement._id) ?? null,
            }
          : requirement
      )
    );
  };

  const goToMatches = (requirementId = "all") => {
    updateParams({
      step: "matches",
      requirement: requirementId === "all" ? null : requirementId,
    });
  };

  const goToRequirements = () => updateParams({ step: null, requirement: null });

  const setSelectedRequirementId = (id) =>
    updateParams({ requirement: id === "all" ? null : id });

  // Same endpoint the requirements tab in Myproperty deletes through. On
  // success we just bump reloadKey - requirements and matches both refetch, and
  // selectedRequirementId falls back to "all" on its own if the deleted
  // requirement was the active filter.
  const handleDeleteRequirement = async () => {
    if (!deletingRequirement?._id) return;

    setDeleting(true);

    const token = Cookies.get("token");
    const result = await apiClient(
      "DELETE",
      `/cjidnvij/ceksfbuebijn/user/brokerrequirements/${deletingRequirement._id}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setDeleting(false);

    if (!result?.success) {
      toast.error(
        result?.error?.message ||
          (typeof result?.error === "string" ? result.error : null) ||
          "Failed to delete requirement"
      );
      return;
    }

    toast.success("Requirement deleted");
    setDeletingRequirement(null);
    setReloadKey((key) => key + 1);
  };

  useEffect(() => {
    let cancelled = false;

    const fetchAllMatches = async () => {
      setLoading(true);
      setError(null);
      setFailedCount(0);
      setBuckets(EMPTY_BUCKETS);
      setRequirements([]);

      const token = Cookies.get("token");
      const authConfig = { headers: { Authorization: `Bearer ${token}` } };

      const requirementsResult = await apiClient(
        "GET",
        "/cjidnvij/ceksfbuebijn/user/brokerrequirements/my-requirements",
        {},
        authConfig
      );

      if (cancelled) return;

      if (!requirementsResult.success) {
        setError(
          requirementsResult.error?.message ||
            (typeof requirementsResult.error === "string"
              ? requirementsResult.error
              : "Failed to load your requirements")
        );
        setLoading(false);
        return;
      }

      const myRequirements = extractRequirements(requirementsResult).filter(
        (requirement) => requirement?._id
      );

      setRequirements(myRequirements);

      if (myRequirements.length === 0) {
        setLoading(false);
        return;
      }

      // Accumulate outside state so overlapping responses cannot clobber each
      // other, then flush a fresh copy after each requirement resolves.
      const accumulator = { owner: new Map(), broker: new Map(), builder: new Map() };

      const flush = () => {
        if (cancelled) return;
        setBuckets({
          owner: Array.from(accumulator.owner.values()),
          broker: Array.from(accumulator.broker.values()),
          builder: Array.from(accumulator.builder.values()),
        });
      };

      const absorb = (bucketKey, list, requirement) => {
        const map = accumulator[bucketKey];
        (list || []).forEach((item, index) => {
          if (!item) return;
          const key = item._id || item.id || `${requirement._id}-${bucketKey}-${index}`;
          const source = { id: requirement._id, label: requirementTag(requirement) };
          const existing = map.get(key);

          if (existing) {
            if (!existing._matchedFor.some((entry) => entry.id === source.id)) {
              existing._matchedFor.push(source);
            }
            return;
          }

          map.set(key, { ...item, _matchedFor: [source] });
        });
      };

      const results = await runWithConcurrency(
        myRequirements,
        MATCH_CONCURRENCY,
        async (requirement) => {
          const result = await apiClient(
            "GET",
            `/cjidnvij/ceksfbuebijn/user/match/${requirement._id}`,
            {},
            authConfig
          );

          if (!result.success) throw new Error("match failed");

          const matched = extractBuckets(result);
          absorb("owner", matched.owner, requirement);
          absorb("broker", matched.broker, requirement);
          absorb("builder", matched.builder, requirement);
          flush();
          return true;
        }
      );

      if (cancelled) return;

      setFailedCount(results.filter((entry) => !entry?.ok).length);
      setLoading(false);
    };

    fetchAllMatches();

    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // Filtering by requirement is purely client side - every property already
  // carries the requirements it matched.
  const visibleBuckets = useMemo(() => {
    if (selectedRequirementId === "all") return buckets;

    const keep = (list) =>
      list.filter((item) =>
        item._matchedFor?.some((entry) => entry.id === selectedRequirementId)
      );

    return {
      owner: keep(buckets.owner),
      broker: keep(buckets.broker),
      builder: keep(buckets.builder),
    };
  }, [buckets, selectedRequirementId]);

  const totalAllMatches =
    buckets.owner.length + buckets.broker.length + buckets.builder.length;

  const totalMatches =
    visibleBuckets.owner.length +
    visibleBuckets.broker.length +
    visibleBuckets.builder.length;

  const countForRequirement = useCallback(
    (requirementId) =>
      BUCKET_KEYS.reduce(
        (sum, bucketKey) =>
          sum +
          buckets[bucketKey].filter((item) =>
            item._matchedFor?.some((entry) => entry.id === requirementId)
          ).length,
        0
      ),
    [buckets]
  );

  const matchedRequirementCount = useMemo(() => {
    const ids = new Set();
    BUCKET_KEYS.forEach((bucketKey) => {
      buckets[bucketKey].forEach((item) =>
        item._matchedFor?.forEach((entry) => ids.add(entry.id))
      );
    });
    return ids.size;
  }, [buckets]);

  // Transform matched property data to match PropertyCard expectations
  const transformPropertyData = (property, sourceType = "owner") => {
    if (!property) return {};

    // Enhanced price/rent logic for different property types and sources
    const getPriceValue = () => {
      // For builder properties with priceRange
      if (sourceType === "builders" && property?.priceRange) {
        return property?.priceRange.min || property?.priceRange.max || "NA";
      }

      // For rent properties
      if (
        property?.type?.includes("Rent") ||
        property?.propertyType?.includes("Rent")
      ) {
        return property?.price || property?.rent || property?.rentValue || "NA";
      }
      // For sell properties
      if (
        property?.type?.includes("Sell") ||
        property?.propertyType?.includes("Sell")
      ) {
        return property?.price || property?.expectedPrice || "NA";
      }
      // Fallback
      return (
        property?.price ||
        property?.rent ||
        property?.rentValue ||
        property?.expectedPrice ||
        "NA"
      );
    };

    const getRentValue = () => {
      // For builder properties with priceRange
      if (sourceType === "builders" && property?.priceRange) {
        return property.priceRange.min || property.priceRange.max || "NA";
      }

      // For rent properties, return the rent value
      if (
        property?.type?.includes("Rent") ||
        property?.propertyType?.includes("Rent")
      ) {
        return property?.price || property?.rent || property?.rentValue || "NA";
      }
      // For sell properties, this might be empty or contain different data
      return property?.rent || property?.rentValue || "NA";
    };

    return {
      _id: property._id || property.id,
      propertyId: property.propertyId || property._id,
      title: property.title || property.propertyTitle || "No Title",
      type: property.type || property.propertyType || "Not Available",
      price: getPriceValue(),
      rentValue: getRentValue(),
      unitType: property.unitType || property.subType || "NA",
      area: property.area || property.location || "NA",
      bhk: property.bhk || property.bedrooms || "NA",
      furnishedType: property.furnishedType || property.furnishing || "NA",
      address: property.address || property.fullAddress || "NA",
      sqFt: property.sqFt || property.squareFt || property.areaSize || "NA",
      description: property.description || property.propertyDescription || "NA",
      listedDate: property.listedDate || property.createdAt || new Date(),
      verifyStatus: property.verifyStatus || "unverified",
      media: property.media || property.images || [],
      name: property.ownerName || property.brokerName || property.name || "NA",
      number:
        property.contactNumber || property.phone || property.number || "NA",

      // Add source information for contact fetching
      listedBy: sourceType,
      ownerName: property.ownerName,
      brokerName: property.brokerName,
      builderName: property.builderName,
      // Add propertyType to help PropertyCard identify the source
      propertyType: sourceType,

      // Include priceRange for builder properties
      priceRange: property.priceRange,
    };
  };

  /* ---------------------------- Step 1: requirements --------------------- */

  const renderRequirementCard = (requirement) => {
    const matchCount = countForRequirement(requirement._id);
    const unreadCount = unreadCountForRequirement(requirement._id);
    const cid = requirementCid(requirement);
    const areas = Array.isArray(requirement.areas) ? requirement.areas : [];
    const bhkList = Array.isArray(requirement.bhk) ? requirement.bhk : [];
    const furnishing = Array.isArray(requirement.furnishingPreferences)
      ? requirement.furnishingPreferences
      : [];

    const locationText = [areas.join(", "), requirement.city]
      .filter(Boolean)
      .join(", ");
    const listedDate = formatDate(requirement.listedDate || requirement.createdAt);
    const sizeText = formatSizeRange(requirement.sizeRange);

    return (
      <div
        key={requirement._id}
        className="relative flex flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
      >
        {/* Unread bubble - clears the moment the broker opens the matches */}
        {unreadCount > 0 ? (
          <span
            title={`${unreadCount} new since you last looked`}
            className="absolute -right-2 -top-2 z-10 flex h-7 min-w-[1.75rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white shadow-md ring-2 ring-white"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}

        {/* CID badge + match count */}
        <div className="mb-3 flex items-start justify-between gap-3">
          {cid ? (
            <span className="rounded-md bg-indigo-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-indigo-600">
              {cid}
            </span>
          ) : (
            <span />
          )}
          <span className="shrink-0 text-sm font-bold text-teal-600">
            {loading ? "…" : `${matchCount} Match${matchCount === 1 ? "" : "es"}`}
          </span>
        </div>

        {/* Heading - customer name */}
        <h3 className="mb-3 text-lg font-bold leading-snug text-gray-900">
          {requirementHeading(requirement)}
        </h3>

        {/* Detail rows */}
        <div className="space-y-2.5 text-sm text-gray-500">
          {requirement.type ? (
            <div className="flex items-center gap-2.5">
              <Tag size={16} className="shrink-0 text-gray-400" />
              <span>{requirement.type}</span>
            </div>
          ) : null}

          {locationText ? (
            <div className="flex items-start gap-2.5">
              <MapPin size={16} className="mt-0.5 shrink-0 text-gray-400" />
              <span>{locationText}</span>
            </div>
          ) : null}

          {listedDate ? (
            <div className="flex items-center gap-2.5">
              <CalendarDays size={16} className="shrink-0 text-gray-400" />
              <span>Listed: {listedDate}</span>
            </div>
          ) : null}

          {/* Premise name - the customer is the heading, so it sits here */}
          {requirement.title ? (
            <div className="flex items-start gap-2.5">
              <Building2 size={16} className="mt-0.5 shrink-0 text-gray-400" />
              <span>{requirement.title}</span>
            </div>
          ) : null}

          {bhkList.length > 0 || furnishing.length > 0 ? (
            <div className="flex items-start gap-2.5">
              <Home size={16} className="mt-0.5 shrink-0 text-gray-400" />
              <span>{[...bhkList, ...furnishing].join(", ")}</span>
            </div>
          ) : null}
        </div>

        {/* Price */}
        <p className="mt-4 text-base font-bold text-gray-900">
          {formatPriceRange(requirement.priceRange)}
        </p>

        <div className="mt-auto pt-4">
          <div className="border-t border-gray-100" />
        </div>

        {/* Subtype / size footnote, then the action */}
        <p className="my-3 text-center text-xs text-gray-400">
          {[requirement.subType, sizeText].filter(Boolean).join(" · ") ||
            "No size specified"}
        </p>

        <div className="flex items-stretch gap-2">
          <button
            onClick={() => goToMatches(requirement._id)}
            className="gradient-primary flex flex-1 items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-primary-foreground shadow-md transition-opacity hover:opacity-90"
          >
            <Search size={16} />
            View Matches
          </button>

          <button
            onClick={() => setEditingRequirement(requirement)}
            title="Edit requirement"
            aria-label="Edit requirement"
            className="rounded-xl border border-gray-200 px-3 text-gray-500 transition-colors hover:border-[#503691] hover:text-[#503691]"
          >
            <Pencil size={16} />
          </button>

          <button
            onClick={() => setDeletingRequirement(requirement)}
            title="Delete requirement"
            aria-label="Delete requirement"
            className="rounded-xl border border-gray-200 px-3 text-gray-500 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    );
  };

  const renderRequirementsStep = () => {
    if (error) {
      return (
        <div className="rounded-2xl border border-gray-200 bg-white py-12 text-center">
          <p className="mb-4 text-gray-700">{error}</p>
          <Button onClick={() => setReloadKey((key) => key + 1)} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
        </div>
      );
    }

    if (loading && requirements.length === 0) {
      return (
        <div className="flex h-48 items-center justify-center text-gray-600">
          Loading your requirements...
        </div>
      );
    }

    if (requirements.length === 0) {
      return (
        <div className="rounded-2xl border border-gray-200 bg-white py-12 text-center">
          <p className="mb-1 font-medium text-gray-700">
            You have no requirements yet.
          </p>
          <p className="mb-5 text-sm text-gray-500">
            Add a requirement and we will match properties to it automatically.
          </p>
          <Button onClick={() => setShowAddModal(true)} className="gap-1.5">
            <Plus size={16} /> Add Requirement
          </Button>
        </div>
      );
    }

    return (
      <>
        {/* Keep requirement cards comfortably wide while preserving the responsive grid. */}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,340px),1fr))] gap-4">
          {requirements.map(renderRequirementCard)}
        </div>

        {/* <div className="flex justify-end">
          <Button onClick={() => goToMatches("all")} className="gap-1.5">
            View All Matched Properties ({totalAllMatches})
            <ArrowRight size={16} />
          </Button>
        </div> */}
      </>
    );
  };

  /* ---------------------------- Step 2: matches -------------------------- */

  // New relative to the cutoff this requirement had when the broker clicked in,
  // not the cutoff we just wrote - otherwise nothing would ever look new.
  const isNewProperty = (property) => {
    if (locallyReadProperties.has(property._id || property.id)) return false;
    const matchedAt = propertyMatchedAt(property);

    return (property._matchedFor || []).some((entry) => {
      const baseline = unreadBaselineRef.current[entry.id];
      return matchedAt > (baseline ? new Date(baseline) : seenSince(entry.id));
    });
  };

  const renderMatchedFor = (property) => {
    const sources = property._matchedFor || [];
    const isNew = isNewProperty(property);

    if (sources.length === 0 && !isNew) return null;

    return (
      <div className="flex items-center justify-between w-full text-xs text-gray-500 gap-2">
        {/* Left: New badge + Matches */}
        <div className="flex flex-wrap items-center gap-1.5">
          {isNew ? (
            <span className="rounded-full bg-red-500 px-2 py-0.5 font-bold text-white">
              New
            </span>
          ) : null}
          {sources.length > 0 ? <span>Matches:</span> : null}
          {sources.slice(0, 3).map((source) => (
            <span
              key={source.id}
              className="rounded-full bg-[#503691]/10 px-2 py-0.5 font-medium text-[#503691]"
            >
              {source.label}
            </span>
          ))}
          {sources.length > 3 ? (
            <span className="text-gray-400">+{sources.length - 3} more</span>
          ) : null}
        </div>

        {/* Right: Mark as read */}
        {isNew ? (
          <button
            onClick={() => {
              sources.forEach((source) => markRequirementSeen(source.id));
              setLocallyReadProperties((prev) => new Set(prev).add(property._id || property.id));
            }}
            className="flex-shrink-0 flex items-center gap-1 rounded-full bg-green-600 px-3 py-1 font-semibold text-white shadow-sm ring-2 ring-green-200 transition-colors hover:bg-green-700 focus:outline-none focus-visible:ring-green-400"
          >
            <CheckCircle2 className="h-3 w-3" />
            <span>Mark as read</span>
          </button>
        ) : null}
      </div>
    );
  };

  const renderPropertyList = (tab) => {
    const list = visibleBuckets[tab.bucket];

    if (list.length === 0) {
      return (
        <div className="py-8 text-center md:py-10">
          <p className="text-sm text-gray-500 md:text-base">
            No {tab.label.toLowerCase()} properties found matching your
            {selectedRequirementId === "all" ? " requirements" : " requirement"}.
          </p>
        </div>
      );
    }

    return list.map((property, index) => (
      <PropertyCard
        key={property._id || property.id || `${tab.key}-${index}`}
        property={transformPropertyData(property, tab.sourceType)}
        contactInfoMap={contactInfoMap}
        setContactInfoMap={setContactInfoMap}
        topHeader={renderMatchedFor(property)}
      />
    ));
  };

  const renderMatchesStep = () => {
    if (error) {
      return (
        <div className="rounded-2xl border border-gray-200 bg-white py-12 text-center">
          <p className="mb-4 text-gray-700">{error}</p>
          <Button onClick={() => setReloadKey((key) => key + 1)} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
        </div>
      );
    }

    if (loading && totalMatches === 0) {
      return (
        <div className="flex h-64 items-center justify-center">
          <div className="text-lg text-gray-600">
            Finding properties that match your requirements...
          </div>
        </div>
      );
    }

    if (!loading && requirements.length === 0) {
      return (
        <div className="rounded-2xl border border-gray-200 bg-white py-12 text-center">
          <p className="mb-4 text-gray-700">
            Add a requirement first to see matched properties.
          </p>
          <Button onClick={goToRequirements} className="gap-1.5">
            <ArrowLeft size={16} /> Back to Requirements
          </Button>
        </div>
      );
    }

    return (

      /* On phones the panel runs edge to edge: -mx-4 cancels the `p-4` gutter
         DashboardLayout puts on <main>, and the corners are squared off since
         they'd otherwise sit at the very screen edge. Unchanged from md up. */
      <div className="-mx-4 rounded-none bg-white p-4 shadow-sm md:mx-0 md:rounded-xl md:p-6">

        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-gray-500">
            <span className="font-semibold text-[#503691]">{totalMatches}</span>{" "}
            {totalMatches === 1 ? "property" : "properties"} matched
            {selectedRequirementId === "all"
              ? ` across ${matchedRequirementCount} of your ${requirements.length} ${
                  requirements.length === 1 ? "requirement" : "requirements"
                }`
              : " for this requirement"}
            {loading ? " so far..." : ""}
          </p>

          <div className="flex items-center gap-2">
            <select
              value={selectedRequirementId}
              onChange={(event) => setSelectedRequirementId(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-[#503691] focus:outline-none md:w-64"
            >
              <option value="all">All requirements ({totalAllMatches})</option>
              {requirements.map((requirement) => {
                const cid = requirementCid(requirement);
                const optionText = cid
                  ? `${cid} · ${requirementLabel(requirement)}`
                  : requirementLabel(requirement);

                return (
                  <option key={requirement._id} value={requirement._id}>
                    {optionText} ({countForRequirement(requirement._id)})
                  </option>
                );
              })}
            </select>
            <button
              onClick={() => setReloadKey((key) => key + 1)}
              disabled={loading}
              title="Refresh matches"
              className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:text-[#503691] disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-4 border-b border-gray-200 md:mb-6">
          <div className="flex space-x-2 overflow-x-auto md:space-x-8">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`whitespace-nowrap border-b-2 px-2 py-3 text-xs font-medium md:px-1 md:py-4 md:text-sm ${
                  activeTab === tab.key
                    ? "border-[#503691] text-[#503691]"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                {tab.label} ({visibleBuckets[tab.bucket].length})
              </button>
            ))}
          </div>
        </div>


        {/* Properties Grid — -mx-4 cancels this panel's own `p-4` so the cards
            reach the screen edge, while the header and tabs above keep it. */}
        <div className="-mx-4 space-y-4 md:mx-0 md:space-y-6">

          {TABS.filter((tab) => tab.key === activeTab).map((tab) => (
            <React.Fragment key={tab.key}>{renderPropertyList(tab)}</React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  /* ------------------------------- Stepper ------------------------------- */

  const renderStepper = () => {
    const steps = [
      { number: 1, label: "Requirements", onClick: goToRequirements },
      { number: 2, label: "Matched Properties", onClick: () => goToMatches("all") },
    ];

    return (
      <div className="flex items-center gap-3">
        {steps.map((item, index) => {
          const isActive = step === item.number;
          return (
            <React.Fragment key={item.number}>
              {index > 0 ? <div className="h-px w-6 bg-gray-300 sm:w-10" /> : null}
              <button
                onClick={item.onClick}
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                  isActive ? "text-[#503691]" : "text-gray-400 hover:text-gray-600"
                }`}
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                    isActive ? "bg-[#503691] text-white" : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {item.number}
                </span>
                <span className="hidden sm:inline">{item.label}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* On mobile the action sits on the stepper row, right aligned. From md up
          it moves to the right of the whole title + stepper block. */}
      <div className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-2">
        <h1 className="col-span-2 col-start-1 row-start-1 text-2xl font-bold md:col-span-1">
          Match Requirement
        </h1>

        <div className="col-start-1 row-start-2">{renderStepper()}</div>

        <div className="col-start-2 row-start-2 justify-self-end md:row-span-2 md:row-start-1 md:self-center">
          {step === 1 ? (
            <Button
              onClick={() => setShowAddModal(true)}
              className="gradient-primary gap-1.5 border-0 text-primary-foreground shadow-md"
            >
              <Plus size={16} /> Add Requirement
            </Button>
          ) : (
            <Button onClick={goToRequirements} variant="outline" className="gap-1.5">
              <ArrowLeft size={16} /> Back to Requirements
            </Button>
          )}
        </div>
      </div>

      {/* Partial failure - some requirements matched, others did not */}
      {failedCount > 0 ? (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Couldn't load matches for {failedCount} of {requirements.length}{" "}
            requirements. Showing the rest.
          </span>
        </div>
      ) : null}

      {step === 1 ? renderRequirementsStep() : renderMatchesStep()}

      {/* One modal serves both flows - passing a requirement puts it in edit mode. */}
      <AddRequirementModal
        open={showAddModal || Boolean(editingRequirement)}
        requirement={editingRequirement}
        onClose={() => {
          setShowAddModal(false);
          setEditingRequirement(null);
        }}
        onCreated={() => setReloadKey((key) => key + 1)}
      />

      {deletingRequirement ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900">
              Delete this requirement?
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              {requirementHeading(deletingRequirement)}
              {requirementCid(deletingRequirement)
                ? ` (${requirementCid(deletingRequirement)})`
                : ""}{" "}
              will be removed along with its matches. This cannot be undone.
            </p>

            <div className="mt-6 flex gap-3">
              <Button
                variant="outline"
                onClick={() => setDeletingRequirement(null)}
                disabled={deleting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteRequirement}
                disabled={deleting}
                className="flex-1 bg-red-600 text-white hover:bg-red-700"
              >
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MatchProperty;
